import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Save, TrendingUp, Dumbbell, ArrowLeft, Info, Copy, ClipboardPaste } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loader, Empty, btn } from "@/components/Page";
import { SKILLS, SKILL_META } from "@/lib/game";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const FULL_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const REST_DAY = 6; // Sunday

export default function ArenaCircuitTraining() {
  const navigate = useNavigate();
  const [fighters, setFighters] = useState([]);
  const [selectedFighter, setSelectedFighter] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [clipboard, setClipboard] = useState(() => {
    try { return JSON.parse(localStorage.getItem("arena_train_clipboard") || "null"); } catch { return null; }
  });

  useEffect(() => {
    loadFighters();
  }, []);

  const loadFighters = async () => {
    try {
      const { data } = await api.get("/arena/fighters");
      setFighters(data.fighters);
      if (data.fighters.length > 0) {
        selectFighter(data.fighters[0]);
      }
    } catch (e) {
      toast.error(apiErr(e));
      setFighters([]);
    } finally {
      setLoading(false);
    }
  };

  const selectFighter = async (fighter) => {
    try {
      const { data } = await api.get(`/arena/fighters/${fighter.id}/training`);
      setSelectedFighter(data.fighter);
      setSchedule(toEditor(data.schedule));
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const toEditor = (src) => {
    const s = [];
    for (let i = 0; i < 7; i++) {
      const d = (src && src[i]) || [];
      s.push([d[0] || "", d[1] || "", d[2] || ""]);
    }
    return s;
  };

  const setSlot = (day, idx, val) => {
    setSchedule((prev) =>
      prev.map((d, i) => (i === day ? d.map((v, j) => (j === idx ? val : v)) : d))
    );
  };

  const currentPayload = () => schedule.map((d) => [d[0] || "", d[1] || "", d[2] || ""]);

  const save = async () => {
    if (!selectedFighter) return;

    setBusy(true);
    try {
      const { data } = await api.put(`/arena/fighters/${selectedFighter.id}/schedule`, {
        schedule: currentPayload(),
      });
      toast.success(data.message, { icon: <TrendingUp size={16} /> });
      setSchedule(toEditor(data.schedule));
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const copySchedule = (schedule, label) => {
    const clean = schedule.map((d) => [d[0] || "", d[1] || "", d[2] || ""]);
    setClipboard(clean);
    localStorage.setItem("arena_train_clipboard", JSON.stringify(clean));
    toast.success(`Copied ${label} — switch fighter and paste`);
  };

  const pasteClipboard = () => {
    if (!clipboard) return toast.error("Clipboard is empty");
    setSchedule(toEditor(clipboard));
    toast.success("Pasted — review and Save Schedule to apply");
  };

  if (loading) return <Loader />;

  if (fighters.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={Dumbbell}
          title="Arena Training"
          subtitle="Train your arena gladiators"
        />
        <Empty label="No Gladiators">
          <p className="text-sm text-zinc-400 mb-4">
            Create an Arena Gladiator to start training
          </p>
          <button
            onClick={() => navigate("/arena-circuit/create-fighter")}
            className={btn()}
          >
            Create Gladiator
          </button>
        </Empty>
      </div>
    );
  }

  const statColor = (skill) => {
    const val = selectedFighter?.stats?.[skill] || 100;
    if (val >= 250) return "text-emerald-400";
    if (val >= 200) return "text-amber-400";
    if (val >= 150) return "text-orange-400";
    return "text-zinc-400";
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/arena-circuit")}
          className={btn() + " bg-zinc-800 hover:bg-zinc-700"}
        >
          <ArrowLeft size={16} />
        </button>
        <PageHeader
          icon={Dumbbell}
          title="Arena Training"
          subtitle="Set training schedules for your gladiators"
        />
      </div>



      {/* Fighter Selection */}
      <div className="flex gap-3 overflow-x-auto pb-2">
        {fighters.map((fighter) => (
          <button
            key={fighter.id}
            onClick={() => selectFighter(fighter)}
            className={`flex-shrink-0 px-4 py-3 border transition-all ${
              selectedFighter?.id === fighter.id
                ? "border-amber-500 bg-amber-900/20"
                : "border-zinc-800 hover:border-amber-700/50 bg-zinc-950"
            }`}
          >
            <div className="font-cinzel text-amber-300 text-sm">{fighter.name}</div>
            <div className="text-xs text-zinc-500 capitalize">{fighter.cls}</div>
          </button>
        ))}
      </div>

      {selectedFighter && (
        <>
          {/* Current Stats */}
          <div className="border border-amber-900/30 bg-zinc-950 p-5">
            <h3 className="font-cinzel text-lg text-amber-300 mb-4 uppercase tracking-wide">
              Current Stats
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {SKILLS.map((skill) => (
                <div key={skill} className="text-center">
                  <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">
                    {skill}
                  </div>
                  <div className={`text-2xl font-cinzel tabular-nums ${statColor(skill)}`}>
                    {selectedFighter.stats[skill]}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Training Schedule */}
          <div className="border border-amber-900/30 bg-zinc-950 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-cinzel text-lg text-amber-300 uppercase tracking-wide">
                Training Schedule
              </h3>
              <div className="flex gap-2">
                <button
                  data-testid="arena-copy-current-btn"
                  onClick={() => copySchedule(currentPayload(), "current schedule")}
                  className={btn() + " text-sm bg-zinc-800 hover:bg-zinc-700"}
                  title="Copy current schedule to clipboard"
                >
                  <Copy size={14} className="mr-2" />
                  Copy
                </button>
                <button
                  data-testid="arena-paste-btn"
                  onClick={pasteClipboard}
                  disabled={!clipboard}
                  className={btn() + " text-sm bg-zinc-800 hover:bg-zinc-700"}
                  title="Paste schedule from clipboard"
                >
                  <ClipboardPaste size={14} className="mr-2" />
                  Paste{clipboard ? "" : " (empty)"}
                </button>
                <button
                  onClick={() => navigate("/arena-circuit/training-summary")}
                  className={btn() + " text-sm bg-zinc-800 hover:bg-zinc-700"}
                >
                  <TrendingUp size={14} className="mr-2" />
                  View Summary
                </button>
                <button onClick={save} className={btn()} disabled={busy}>
                  <Save size={16} className="mr-2" />
                  {busy ? "Saving..." : "Save Schedule"}
                </button>
              </div>
            </div>

            <p className="text-[11px] text-zinc-500 mb-3">
              Use Copy/Paste to transfer a schedule to another arena gladiator (then Save Schedule to apply).
            </p>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="text-left font-cinzel text-sm text-zinc-400 uppercase tracking-wide pb-3">
                      Day
                    </th>
                    <th className="text-left font-cinzel text-sm text-zinc-400 uppercase tracking-wide pb-3">
                      Slot 1
                    </th>
                    <th className="text-left font-cinzel text-sm text-zinc-400 uppercase tracking-wide pb-3">
                      Slot 2
                    </th>
                    <th className="text-left font-cinzel text-sm text-zinc-400 uppercase tracking-wide pb-3">
                      Slot 3
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.map((day, dayIdx) => (
                    <tr key={dayIdx} className="border-t border-zinc-800">
                      <td className="py-3 pr-4">
                        <div className="font-cinzel text-amber-300">
                          {FULL_DAYS[dayIdx]}
                        </div>
                        <div className="text-xs text-zinc-500">{DAYS[dayIdx]}</div>
                      </td>
                      {dayIdx === REST_DAY ? (
                        <td colSpan={3} className="py-3 text-zinc-500 text-sm italic">
                          Rest Day - No Training
                        </td>
                      ) : (
                        day.map((skill, slotIdx) => (
                          <td key={slotIdx} className="py-3 pr-3">
                            <select
                              value={skill}
                              onChange={(e) => setSlot(dayIdx, slotIdx, e.target.value)}
                              className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm text-zinc-200 focus:border-amber-600 outline-none capitalize"
                            >
                              <option value="">-- None --</option>
                              {SKILLS.map((s) => (
                                <option key={s} value={s} className="capitalize">
                                  {s}
                                </option>
                              ))}
                            </select>
                          </td>
                        ))
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
